import axios from "axios";

const STOP_ROUTE_MIN_EXTRA_KM = 25;
const STOP_ROUTE_EXTRA_PERCENT = 0.2;

export const STOP_OFF_ROUTE_MESSAGE =
  "This stop is too far from your route. Please add a stop between pick-up and drop-off.";

function hasCoords(place) {
  if (!place) return false;
  // null/undefined must not pass: Number(null) === 0 and would treat missing
  // re-book stop coords as Null Island (0,0), falsely failing route validation.
  if (place.lat == null || place.lng == null || place.lat === "" || place.lng === "") {
    return false;
  }
  const lat = Number(place.lat);
  const lng = Number(place.lng);
  return Number.isFinite(lat) && Number.isFinite(lng);
}

function distanceKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/** Fallback when the validate-stop API is unreachable. */
function getStopRouteValidationErrorHaversine(pickup, drop, stop) {
  if (!hasCoords(pickup) || !hasCoords(drop) || !hasCoords(stop)) {
    return null;
  }

  const directKm = distanceKm(
    Number(pickup.lat),
    Number(pickup.lng),
    Number(drop.lat),
    Number(drop.lng),
  );
  if (directKm < 1) return null;

  const viaStopKm =
    distanceKm(
      Number(pickup.lat),
      Number(pickup.lng),
      Number(stop.lat),
      Number(stop.lng),
    ) +
    distanceKm(
      Number(stop.lat),
      Number(stop.lng),
      Number(drop.lat),
      Number(drop.lng),
    );
  const extraKm = viaStopKm - directKm;
  const maxExtraKm = Math.max(
    STOP_ROUTE_MIN_EXTRA_KM,
    directKm * STOP_ROUTE_EXTRA_PERCENT,
  );

  if (extraKm > maxExtraKm) {
    return STOP_OFF_ROUTE_MESSAGE;
  }

  return null;
}

/**
 * Google driving-route check via backend (same as fare distance).
 * @returns {Promise<string|null>} Error message or null if allowed.
 */
export async function getStopRouteValidationError(pickup, drop, stop) {
  if (!hasCoords(pickup) || !hasCoords(drop) || !hasCoords(stop)) {
    return null;
  }

  try {
    const { data } = await axios.post(
      `${process.env.REACT_APP_APP_DOMAIN}/api/customer/validate-stop-on-route`,
      {
        pickup_lat: Number(pickup.lat),
        pickup_lng: Number(pickup.lng),
        drop_lat: Number(drop.lat),
        drop_lng: Number(drop.lng),
        stop_lat: Number(stop.lat),
        stop_lng: Number(stop.lng),
      },
    );
    if (data?.status === true) {
      return null;
    }
    return data?.message || STOP_OFF_ROUTE_MESSAGE;
  } catch (error) {
    const apiMessage = error.response?.data?.message;
    if (apiMessage) {
      return apiMessage;
    }
    return getStopRouteValidationErrorHaversine(pickup, drop, stop);
  }
}

export async function validateStopsOnRoute(pickup, drop, stops = []) {
  for (let index = 0; index < stops.length; index++) {
    const stop = stops[index];
    if (!stop?.address?.trim() || !hasCoords(stop)) continue;
    const message = await getStopRouteValidationError(pickup, drop, stop);
    if (message) return { index, message };
  }
  return null;
}
